/**
 * The description renderer, per video type. Browser-safe, pure.
 *
 * The model writes the prose (summary, points, chapter headings and
 * summaries, about); code adds every heading, timestamp, credit line and
 * link, so the format is fixed and never drifts:
 *
 *   summary paragraphs
 *   Presented by … (talks) / Panelists: … (panels)
 *   KEY TAKEAWAYS | WHAT YOU'LL LEARN | WHAT YOU'LL SEE
 *   CHAPTERS (m:ss heading, then a one-sentence summary)
 *   ABOUT <person / organization / product>
 *   Originally aired on <outlet>. (media appearances only)
 *   MENTIONED IN THIS VIDEO (sources and tools, when given)
 *   CONNECT (only the links we have; never a placeholder)
 */

import type {
	DescriptionChapter,
	DescriptionLink,
	DescriptionParts,
	GeneratedOutput,
	PointsHeading,
	TranscriptSegment,
	VideoPerson,
	VideoType,
} from "./types";

export const POINTS_HEADING_BY_TYPE: Record<VideoType, PointsHeading> = {
	"media-appearance": "KEY TAKEAWAYS",
	talk: "WHAT YOU'LL LEARN",
	"product-demo": "WHAT YOU'LL SEE",
	panel: "KEY TAKEAWAYS",
	other: "KEY TAKEAWAYS",
};

/** m:ss under an hour, h:mm:ss above. YouTube's chapter format. */
export function formatChapterTimestamp(totalSec: number): string {
	const sec = Math.max(0, Math.floor(totalSec + 1e-6));
	const h = Math.floor(sec / 3600);
	const m = Math.floor((sec % 3600) / 60);
	const ss = String(sec % 60).padStart(2, "0");
	return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** YouTube counts the description limit in UTF-8 bytes. */
export function utf8ByteLength(text: string): number {
	return new TextEncoder().encode(text).length;
}

/** "Name, role at Organization" (or "Name, role of Organization"). */
export function formatPersonCredit(person: VideoPerson): string {
	const role = person.role?.trim();
	const org = person.organization?.trim();
	if (role && org) {
		if (/\b(of|at)\b/i.test(role)) return `${person.name}, ${role}`;
		const joiner = /founder|ceo|president|owner|chief/i.test(role)
			? "of"
			: "at";
		return `${person.name}, ${role} ${joiner} ${org}`;
	}
	return [person.name, role, org].filter(Boolean).join(", ");
}

const URL_PATTERN = /https?:\/\/[^\s)<>"]+/g;

/** Strip trailing punctuation a sentence may have glued onto a URL. */
function cleanUrl(url: string): string {
	return url.replace(/[.,;:!?)\]]+$/, "");
}

/**
 * Parse a block of "Label: URL" lines (a bare URL is fine too; the label then
 * falls back to the host). Lines without a URL are kept as label-only
 * entries.
 */
export function parseLinkLines(
	block: string | null | undefined,
): DescriptionLink[] {
	if (!block) return [];
	const out: DescriptionLink[] = [];
	for (const rawLine of block.split(/\r?\n/)) {
		const line = rawLine.trim().replace(/^[-•*]\s*/, "");
		if (!line) continue;
		const match = line.match(/https?:\/\/\S+/);
		if (!match) {
			out.push({ label: line });
			continue;
		}
		const url = cleanUrl(match[0]);
		let label = line
			.slice(0, match.index)
			.replace(/[\s:|–-]+$/, "")
			.trim();
		if (!label) {
			try {
				label = new URL(url).hostname.replace(/^www\./, "");
			} catch {
				label = url;
			}
		}
		out.push({ label, url });
	}
	return out;
}

/** Every URL in a text (for the links in the current YouTube description). */
export function extractUrls(text: string | null | undefined): string[] {
	if (!text) return [];
	return [...text.matchAll(URL_PATTERN)].map((m) => cleanUrl(m[0]));
}

const linkKey = (url: string) =>
	url
		.toLowerCase()
		.replace(/^https?:\/\/(www\.)?/, "")
		.replace(/\/$/, "");

export interface DescriptionContext {
	videoType: VideoType;
	/** The featured people (confirmed or detected); may be empty. */
	people: VideoPerson[];
	brandName: string;
	brandWebsite: string | null;
	subjectEntity: string | null;
	/** Shown only for media appearances. */
	outlet: string | null;
	/** Parsed contact links from the form ("Label: URL" lines). */
	contacts: DescriptionLink[];
	/** Parsed sources and tools named in the video. */
	sources: DescriptionLink[];
	/** URLs in the client's current YouTube description. */
	copyLinks: string[];
}

/** The fixed, code-written parts of the description for a context. */
export function describeContext(ctx: DescriptionContext) {
	const { videoType, people } = ctx;
	const aboutName =
		videoType === "product-demo" || people.length === 0
			? ctx.subjectEntity || ctx.brandName
			: people.length === 1
				? (people[0]?.name ?? ctx.brandName)
				: people
						.map((p) => p.name.split(" ").slice(-1)[0] ?? p.name)
						.join(" AND ");

	const connect: Array<{ label: string; url: string }> = [];
	const seen = new Set<string>();
	const add = (label: string, url: string) => {
		const key = linkKey(url);
		if (seen.has(key)) return;
		seen.add(key);
		connect.push({ label, url });
	};
	for (const c of ctx.contacts) if (c.url) add(c.label, c.url);
	if (ctx.brandWebsite) add(ctx.brandName, ctx.brandWebsite);
	if (ctx.contacts.length === 0) {
		for (const url of ctx.copyLinks) {
			const key = linkKey(url);
			const person = people.find((p) => {
				const surname = p.name.split(" ").slice(-1)[0]?.toLowerCase();
				return surname ? key.includes(surname) : false;
			});
			add(person ? person.name : ctx.brandName, url);
		}
	}

	return {
		pointsHeading: POINTS_HEADING_BY_TYPE[videoType],
		presentedBy:
			videoType === "talk" && people.length
				? people.map(formatPersonCredit).join(" and ")
				: null,
		panelists:
			videoType === "panel" && people.length
				? people.map(formatPersonCredit).join("; ")
				: null,
		aboutHeading: `ABOUT ${aboutName.toUpperCase()}`,
		outlet: videoType === "media-appearance" ? ctx.outlet || null : null,
		mentioned: ctx.sources,
		connect,
	};
}

/** "s012" → 12. */
export function parseSegmentRef(ref: string | number): number {
	if (typeof ref === "number") return ref;
	const m = String(ref ?? "").match(/(\d+)/);
	return m ? Number.parseInt(m[1] ?? "", 10) : Number.NaN;
}

/** "s012" for segment 12, the id the model sees. */
export function segmentRef(id: number): string {
	return `s${String(id).padStart(3, "0")}`;
}

/** Chapter times from segment ids; the first chapter is forced to 0:00. */
export function deriveChapters(
	chapters: GeneratedOutput["chapters"],
	segments: readonly Pick<TranscriptSegment, "id" | "startSec">[],
): DescriptionChapter[] {
	const byId = new Map(segments.map((s) => [s.id, s]));
	return chapters.map((c, i) => {
		const segmentId = parseSegmentRef(c.segmentId);
		const start = byId.get(segmentId)?.startSec;
		return {
			sec:
				i === 0
					? 0
					: start === undefined
						? Number.NaN
						: Math.floor(start),
			segmentId,
			heading: c.heading.trim(),
			summary: c.summary.trim(),
		};
	});
}

/** Model output + context → the render-ready parts. */
export function buildDescriptionParts(
	generated: GeneratedOutput,
	segments: readonly Pick<TranscriptSegment, "id" | "startSec">[],
	ctx: DescriptionContext,
): DescriptionParts {
	const fixed = describeContext(ctx);
	return {
		videoType: ctx.videoType,
		summary: generated.summaryParagraphs
			.map((p) => p.trim())
			.filter(Boolean),
		presentedBy: fixed.presentedBy,
		panelists: fixed.panelists,
		pointsHeading: fixed.pointsHeading,
		points: generated.takeaways.map((t) => t.text.trim()).filter(Boolean),
		chapters: deriveChapters(generated.chapters, segments),
		aboutHeading: fixed.aboutHeading,
		about: generated.about.trim(),
		outlet: fixed.outlet,
		mentioned: fixed.mentioned,
		connect: fixed.connect,
	};
}

/** Render the parts into the paste-ready description. */
export function renderVideoDescription(parts: DescriptionParts): string {
	const blocks: string[] = [parts.summary.join("\n\n")];
	if (parts.presentedBy) blocks.push(`Presented by ${parts.presentedBy}.`);
	if (parts.panelists) blocks.push(`Panelists: ${parts.panelists}.`);
	blocks.push(
		[parts.pointsHeading, ...parts.points.map((p) => `- ${p}`)].join("\n"),
	);
	blocks.push(
		[
			"CHAPTERS",
			parts.chapters
				.map(
					(c) =>
						`${formatChapterTimestamp(Number.isNaN(c.sec) ? 0 : c.sec)} ${c.heading}\n${c.summary}`,
				)
				.join("\n\n"),
		].join("\n"),
	);
	blocks.push(`${parts.aboutHeading}\n${parts.about}`);
	if (parts.outlet) blocks.push(`Originally aired on ${parts.outlet}.`);
	if (parts.mentioned.length > 0) {
		blocks.push(
			[
				"MENTIONED IN THIS VIDEO",
				...parts.mentioned.map((m) =>
					m.url ? `${m.label}: ${m.url}` : m.label,
				),
			].join("\n"),
		);
	}
	if (parts.connect.length > 0) {
		blocks.push(
			[
				"CONNECT",
				...parts.connect.map((c) => `${c.label}: ${c.url}`),
			].join("\n"),
		);
	}
	return blocks.join("\n\n");
}

/** Bytes of the text the model wrote (for the repair's length budget). */
export function writtenTextBytes(parts: DescriptionParts): number {
	return utf8ByteLength(
		[
			...parts.summary,
			...parts.points,
			...parts.chapters.flatMap((c) => [c.heading, c.summary]),
			parts.about,
		].join(""),
	);
}
